import React, { useState, useEffect, useRef } from "react";
import { Navbar, Nav, Container, Offcanvas } from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";
import { FiArrowUpRight, FiMenu, FiX } from "react-icons/fi";
import logo from "../../Assets/logo-white.webp";
import darkLogo from "../../Assets/logo.webp";
import { navigationLinks } from "../../data/navigation";
import { CONTACT_FORM_EMAIL } from "../../utils/contactFormSubmission";

export default function NavBar() {
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setExpanded(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) setExpanded(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const closeMenu = () => setExpanded(false);

  const wrapMenuFocus = (event) => {
    if (!expanded || event.key !== "Tab") return;

    const focusable = Array.from(event.currentTarget.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex="0"]'
    )).filter((element) => element.getClientRects().length > 0);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && (document.activeElement === first || document.activeElement === event.currentTarget)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  return (
    <Navbar
      expanded={expanded}
      onToggle={setExpanded}
      fixed="top"
      expand="lg"
      variant="dark"
      className={scrolled ? "sticky" : "navbar"}
    >
      <Container>
        <Navbar.Brand as={NavLink} to="/" end aria-label="Klean Leaf home">
          <img src={logo} alt="Klean Leaf logo" className="img-fluid logo" />
        </Navbar.Brand>

        <Navbar.Toggle
          aria-controls="responsive-navbar-nav"
          aria-expanded={expanded}
          label="Open menu"
        >
          <FiMenu aria-hidden="true" focusable="false" />
        </Navbar.Toggle>

        <Navbar.Offcanvas
          id="responsive-navbar-nav"
          className="klean-nav-panel mobile-type"
          placement="end"
          aria-labelledby="mobile-menu-title"
          backdropClassName="klean-nav-backdrop"
          scroll={false}
          restoreFocusOptions={{ preventScroll: true }}
          onEntered={() => closeRef.current?.focus({ preventScroll: true })}
          onKeyDown={wrapMenuFocus}
        >
          <Offcanvas.Header className="mobile-menu-header">
            <NavLink to="/" className="mobile-menu-brand" aria-label="Klean Leaf home" onClick={closeMenu}>
              <img src={darkLogo} alt="" width="44" height="44" />
              <span>Klean Leaf</span>
            </NavLink>
            <span id="mobile-menu-title" className="visually-hidden">Klean Leaf menu</span>
            <button ref={closeRef} type="button" className="mobile-menu-close" aria-label="Close menu" onClick={closeMenu}>
              <FiX aria-hidden="true" focusable="false" />
            </button>
          </Offcanvas.Header>

          <Offcanvas.Body>
            <Nav as="nav" className="ms-auto" aria-label="Primary navigation">
              {navigationLinks.map(({ to, label, end }) => (
                <Nav.Item key={to}>
                  <Nav.Link as={NavLink} to={to} end={end} className="nav-link" onClick={closeMenu}>
                    <span>{label}</span>
                    {to === "/products" && <FiArrowUpRight className="nav-product-arrow" aria-hidden="true" focusable="false" />}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            <div className="mobile-menu-footer">
              <p>Private-label laundry &amp; home care.</p>
              <a href={"mailto:" + CONTACT_FORM_EMAIL}>
                {CONTACT_FORM_EMAIL}
                <FiArrowUpRight aria-hidden="true" focusable="false" />
              </a>
            </div>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </Container>
    </Navbar>
  );
}
